import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axiosConfig';
import { API_ENDPOINTS } from '../config/api';

const REPORT_HEADINGS = new Set([
    'Introduction',
    'Overview (analyzed day)',
    'Daily Performance',
    'Weekly Performance',
    'Rolling 7 days (IST, ending on report date)',
    'Compared to the previous day',
    'Demand',
    'Top items on analyzed day (qty)',
    'Inventory',
    'Inventory quick check',
    'Staffing',
    'Staff quick check',
    'Operations',
    'Operations quick check',
    'Suggestions',
    'Practical Suggestions',
    'Practical next steps',
    'Report',
]);

function renderReportText(summary) {
    const lines = String(summary || '').split(/\r?\n/);
    return lines.map((raw, idx) => {
        const line = raw ?? '';
        const t = line.trim();

        if (!t) {
            return <div key={`sp-${idx}`} className="h-3" />;
        }

        if (REPORT_HEADINGS.has(t)) {
            return (
                <div key={`h-${idx}`} className="text-[15px] sm:text-base font-bold text-stone-900 mt-1">
                    {t}
                </div>
            );
        }

        const isBullet = t.startsWith('•') || t.startsWith('-');
        return (
            <div
                key={`l-${idx}`}
                className={isBullet ? 'pl-2' : undefined}
            >
                {line}
            </div>
        );
    });
}

function formatReportDateIST(reportDate) {
    if (!reportDate) return '';
    // Backend sends YYYY-MM-DD (IST). Parse as an IST midnight instant.
    const d = new Date(`${reportDate}T00:00:00+05:30`);
    if (Number.isNaN(d.getTime())) return String(reportDate);

    const parts = new Intl.DateTimeFormat('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        weekday: 'long',
    }).formatToParts(d);

    const day = parts.find((p) => p.type === 'day')?.value;
    const month = parts.find((p) => p.type === 'month')?.value;
    const year = parts.find((p) => p.type === 'year')?.value;
    const weekday = parts.find((p) => p.type === 'weekday')?.value;

    if (!day || !month || !year) return String(reportDate);
    return `${day}-${month}-${year}${weekday ? ` ${weekday}` : ''}`;
}

const DailyAiSalesSummary = ({ restaurantId }) => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [geminiConfigured, setGeminiConfigured] = useState(false);
    const [groqConfigured, setGroqConfigured] = useState(false);
    const [summaryMode, setSummaryMode] = useState('auto');
    const [summaryDoc, setSummaryDoc] = useState(null);
    const [showReport, setShowReport] = useState(false);

    const load = useCallback(async () => {
        if (!restaurantId) return;
        setLoading(true);
        setError(null);
        try {
            const { data } = await axios.get(API_ENDPOINTS.ANALYTICS_DAILY_AI_SUMMARY(restaurantId));
            setGeminiConfigured(Boolean(data.geminiConfigured));
            setGroqConfigured(Boolean(data.groqConfigured));
            setSummaryMode(data.summaryMode || 'auto');
            setSummaryDoc(data.summary || null);
        } catch (e) {
            setError(e.response?.data?.message || e.message || 'Could not load AI summary');
            setSummaryDoc(null);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
    }, [load]);

    const handleRefresh = async () => {
        if (!restaurantId || refreshing) return;
        setRefreshing(true);
        setError(null);
        try {
            const { data } = await axios.post(
                `${API_ENDPOINTS.ANALYTICS_DAILY_AI_SUMMARY_REFRESH(restaurantId)}?period=today`
            );
            setSummaryDoc(data.summary || null);
        } catch (e) {
            const msg =
                e.response?.data?.message ||
                e.message ||
                (e.response?.status === 429 ? 'Too many AI requests. Wait a minute and try again.' : 'Refresh failed');
            setError(msg);
        } finally {
            setRefreshing(false);
        }
    };

    if (!restaurantId) return null;

    const stats = summaryDoc?.statsSnapshot;
    const reportDate = summaryDoc?.reportDate;
    const reportDateLabel = reportDate ? formatReportDateIST(reportDate) : null;
    const refreshBlocked = summaryMode === 'gemini' && !geminiConfigured;
    const reportRegionId = 'ai-daily-sales-report-region';

    return (
        <section className="w-full">
            <div className="sticky top-0 z-10 bg-white border-b border-stone-200 shadow-sm">
                <div className="px-3 sm:px-0 py-3 sm:py-3">
                    <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-[20px] font-bold uppercase tracking-wide text-stone-700">AI daily sales report</p>
                        <p className="text-[11px] text-stone-400">
                            {reportDateLabel ? `Date: ${reportDateLabel} (IST)` : ''}
               
                            </p>
                            <p className="text-[11px] text-stone-400">
                                (New report generated at 6:00 AM IST)
                            </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() => setShowReport((s) => !s)}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl font-semibold text-xs min-h-[44px] border border-stone-200 bg-white text-stone-700 hover:bg-stone-50 active:scale-[0.98] transition-all focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:ring-offset-2 focus:ring-offset-white"
                            aria-expanded={showReport}
                            aria-controls={reportRegionId}
                        >
                            {showReport ? 'Hide' : 'Show'}
                        </button>
                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={refreshing || refreshBlocked}
                            className="inline-flex items-center justify-center p-2.5 rounded-xl font-semibold text-sm bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-sm min-h-[44px] min-w-[44px] focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:ring-offset-2 focus:ring-offset-white"
                            title="Refresh"
                            aria-label="Refresh"
                        >
                            <span className="text-sm">{refreshing ? '…' : '↻'}</span>
                        </button>
                    </div>
                    </div>
                </div>
            </div>

            <div className="px-3 sm:px-0 space-y-3 mt-3 sm:mt-4">
                {loading && (
                    <div className="flex items-center gap-2 text-slate-600 text-sm">
                        <span className="inline-block h-4 w-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        Loading insight…
                    </div>
                )}

                {!loading && !showReport && (
                    <div className="bg-stone-50/80 border border-stone-200 rounded-lg p-3 text-xs text-stone-600">
                        Report hidden. Tap <strong>Show</strong> to expand.
                    </div>
                )}

                {!loading && showReport && (
                    <div id={reportRegionId} className="space-y-3">
                        {refreshBlocked && (
                            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                                Server is set to Gemini-only (<code className="text-xs bg-amber-100/80 px-1 rounded">SALES_SUMMARY_MODE=gemini</code>) but{' '}
                                <code className="text-xs bg-amber-100/80 px-1 rounded">GEMINI_API_KEY</code> is missing. Use{' '}
                                <code className="text-xs bg-amber-100/80 px-1 rounded">auto</code> or <code className="text-xs bg-amber-100/80 px-1 rounded">local</code> for free summaries.
                            </p>
                        )}

                        {!refreshBlocked && summaryMode === 'local' && (
                            <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                <strong>Template-only mode:</strong> no cloud LLM. Set <code className="text-[11px] bg-white px-1 rounded border">SALES_SUMMARY_MODE=auto</code> and add{' '}
                                <code className="text-[11px] bg-white px-1 rounded border">GROQ_API_KEY</code> (free tier) or <code className="text-[11px] bg-white px-1 rounded border">GEMINI_API_KEY</code> for AI wording.
                            </p>
                        )}

                        {!refreshBlocked && summaryMode === 'groq' && (
                            <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                <strong>Groq mode:</strong> summaries use Groq&apos;s free API tier (rate limits apply). Missing key falls back to the on-server template.
                            </p>
                        )}

                        {!refreshBlocked && summaryMode === 'auto' && !geminiConfigured && !groqConfigured && (
                            <p className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                                <strong>No LLM keys:</strong> using free on-server summaries. Add <code className="text-[11px] bg-white px-1 rounded border">GROQ_API_KEY</code> from{' '}
                                <a href="https://console.groq.com" className="text-indigo-600 underline" target="_blank" rel="noreferrer">
                                    console.groq.com
                                </a>{' '}
                                (free tier) or <code className="text-[11px] bg-white px-1 rounded border">GEMINI_API_KEY</code> for AI text.
                            </p>
                        )}

                        {error && (
                            <p
                                className={`text-sm rounded-lg px-3 py-2 border ${
                                    error.includes('rate limit') || error.includes('Rate limit') || error.includes('quota')
                                        ? 'text-amber-900 bg-amber-50 border-amber-200'
                                        : 'text-red-700 bg-red-50 border-red-100'
                                }`}
                            >
                                {error}
                            </p>
                        )}

                        {!refreshBlocked && !error && !summaryDoc && (
                            <p className="text-sm text-slate-600">
                                No report yet. Tap <strong>Refresh</strong> or wait for the nightly job (yesterday IST).
                            </p>
                        )}

                        {summaryDoc && (
                            <div className="bg-stone-50/80 border border-stone-200 rounded-lg p-3 sm:p-4 space-y-2">
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <p className="text-[16px] font-bold uppercase tracking-wide text-stone-500">Report</p>
                                    {stats && (stats.totalOrders > 0 || stats.totalRevenue > 0) && (
                                        <div className="flex flex-wrap gap-1.5">
                                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-stone-200 text-stone-700">
                                                {stats.totalOrders} orders
                                            </span>
                                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-stone-200 text-stone-700">
                                                ₹{Number(stats.totalRevenue || 0).toFixed(0)} revenue
                                            </span>
                                            {stats.tableOrders != null && stats.counterOrders != null && (
                                                <span className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-stone-200 text-stone-700">
                                                    Tables {stats.tableOrders} · Counter {stats.counterOrders}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="text-[13px] sm:text-sm text-stone-800 leading-7 sm:leading-relaxed max-h-[55vh] overflow-auto pr-1">
                                    {renderReportText(summaryDoc.summary)}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
};

export default DailyAiSalesSummary;
